<template>
  <div class="status-selector" ref="rootEl">
    <button class="trigger" @click.stop="open = !open">
      <span class="dot" :style="{ background: agent.def.color }"></span>
      <span class="label">{{ agent.def.label }}</span>
      <span class="time">{{ elapsed }}</span>
      <Icon name="chevron-down" :size="13" />
    </button>

    <transition name="dd">
      <div v-if="open" class="menu" @click.stop>
        <div class="menu-header">Statut agent</div>
        <button
          v-for="key in SELECTABLE_STATUSES"
          :key="key"
          class="menu-item"
          :class="{ active: key === agent.current }"
          @click="select(key)"
        >
          <span class="dot" :style="{ background: STATUS_DEFS[key].color }"></span>
          <div class="m-text">
            <span class="m-name">{{ STATUS_DEFS[key].label }}</span>
            <span class="m-desc">{{ STATUS_DEFS[key].description }}</span>
          </div>
          <Icon v-if="STATUS_DEFS[key].canReceiveCalls" name="phone-incoming" :size="14" class="m-tag" />
          <Icon v-if="key === agent.current" name="check" :size="14" class="m-check" />
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import Icon from '@/shared/components/Icon.vue'
import { useAgentStatusStore, STATUS_DEFS, SELECTABLE_STATUSES, type AgentStatusKey } from '@/shared/stores/agent_status'

const agent = useAgentStatusStore()
const open = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const now = ref(Date.now())

const elapsed = computed(() => {
  const start = new Date(agent.changedAt).getTime()
  const diff = Math.max(0, now.value - start)
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

function select(key: AgentStatusKey) {
  agent.change(key)
  open.value = false
}

function onClickOutside(e: MouseEvent) {
  if (rootEl.value && !rootEl.value.contains(e.target as Node)) open.value = false
}

let timer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  agent.load()
  document.addEventListener('click', onClickOutside)
  timer = setInterval(() => { now.value = Date.now() }, 1000)
})
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside)
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
.status-selector { position: relative; }

.trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
  transition: all var(--duration-base);
  min-width: 170px;
  font-weight: 500;
}
.trigger:hover:not(:disabled) { border-color: var(--border-strong); background: var(--surface-hover); box-shadow: var(--shadow-xs); }
.trigger:disabled { cursor: not-allowed; opacity: 0.7; }

.dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
}

.label { font-weight: 500; flex: 1; text-align: left; font-size: 13px; }
.time {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-muted);
  background: var(--surface-hover);
  padding: 1px 5px;
  border-radius: var(--radius-xs);
  letter-spacing: 0.02em;
}

.menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 268px;
  z-index: 1000;
  padding: 4px;
  overflow: hidden;
}
.menu-header {
  padding: 9px 12px 6px;
  font-size: 10px;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  border-bottom: 1px solid var(--border);
  margin-bottom: 3px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  text-align: left;
  transition: background var(--duration-fast);
}
.menu-item:hover { background: var(--surface-hover); }
.menu-item.active { background: var(--primary-100); }

.m-text { flex: 1; display: flex; flex-direction: column; line-height: 1.3; }
.m-name { font-size: 13px; font-weight: 600; color: var(--text); }
.m-desc { font-size: 11.5px; color: var(--text-muted); margin-top: 1px; font-weight: 400; }
.m-tag { color: var(--text-muted); flex-shrink: 0; opacity: 0.7; }
.m-check { color: var(--primary); flex-shrink: 0; }

.dd-enter-active, .dd-leave-active { transition: opacity 0.12s, transform 0.12s; }
.dd-enter-from, .dd-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
